<template>
  <div>
    <label
      :class="[
        variant === 'static' ? '' : 'form-label',
        'd-flex',
        'align-items-center'
      ]"
      :for="id"
      v-if="label"
    >
      {{ label }}
      <span v-if="isRequired" class="text-danger">*</span>

      <Tooltip
        v-if="toolTipHeading && toolTipHeading != ''"
        :heading="toolTipHeading"
        :paragraph="toolTipDescription"
      />
    </label>

    <div
      class="input-group"
      :class="[`input-group-${variant}`, getStatus(error, success)]"
    >
      <select
        :id="id || name"
        class="form-control select-with-arrow"
        :class="getClasses(size)"
        :name="name"
        :disabled="disabled || readOnly"
        :required="isRequired"
        v-model="localValue"
        :style="[
          readOnly
            ? {
                backgroundColor: '#e9ecef',
                cursor: 'not-allowed',
                paddingBlock: '0 !important',
              }
            : { paddingBlock: '0 !important' },
          textColorStyle,
        ]"
      >
        <option :value="null" disabled>Select ...</option>
        <option
          v-for="option in options"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, computed } from "vue";
import Tooltip from "./Tooltip.vue";
import setMaterialInput from "@/assets/js/material-input.js";

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, null],
    default: "",
  },
  options: {
    type: Array,
    required: true,
    default: () => [],
  },
  variant: {
    type: String,
    default: "outline",
  },
  label: {
    type: String,
  },
  size: {
    type: String,
    default: "default",
  },
  success: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    // required: true,
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  toolTipHeading: {
    type: String,
    default: "",
  },
  toolTipDescription: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue"]);

const localValue = ref(props.modelValue);

watch(
  () => props.modelValue,
  (newVal) => {
    localValue.value = newVal;
  }
);

watch(localValue, (newVal) => {
  emit("update:modelValue", newVal);
});

const textColorStyle = computed(() => {
  const selectedOption = props.options.find(
    (opt) => opt.value === localValue.value
  );
  if (!selectedOption || !selectedOption.label) return {};

  const label = String(selectedOption.label).toLowerCase().trim();

  if (["enabled", "yes", "active"].includes(label)) {
    return { color: "#4CAF50", fontWeight: "bold" };
  }
  if (["disabled", "no", "inactive"].includes(label)) {
    return { color: "#F44336", fontWeight: "bold" };
  }
  return {};
});

onMounted(() => {
  setMaterialInput();
});

const getClasses = (size) => (size ? `form-control-${size}` : null);
const getStatus = (error, success) => {
  if (success) return "is-valid";
  if (error) return "is-invalid";
  return null;
};
</script>

<style scoped>
.select-with-arrow {
  appearance: auto !important;
  -webkit-appearance: auto !important;
  -moz-appearance: auto !important;
  background-image: none !important;
  padding-right: 2rem;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}
.form-control {
  padding: 0 !important;
}
</style>
